/**
 * Formatters Utility
 * Fiyat, tarih ve diğer veri formatlamaları
 */

/**
 * Fiyatı Türk formatında formatlar
 * Örnek: 1234567.89 -> "1 234 567.89 ₺"
 * Kuruşlar nokta ile, diğer basamaklar boşlukla ayrılır
 */
export const formatPrice = (price, options = {}) => {
  const {
    showCurrency = true,
    showDecimals = true,
    currency = '₺',
  } = options;

  if (price === null || price === undefined || isNaN(price)) {
    return showCurrency ? `0${currency}` : '0';
  }

  const numPrice = parseFloat(price);

  // Tam sayı ve ondalık kısmı ayır
  const [integerPart, decimalPart] = numPrice.toFixed(2).split('.');

  // Tam sayı kısmını boşluklarla ayır (sağdan sola 3'er)
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  // Sonucu birleştir
  let result = formattedInteger;

  if (showDecimals && decimalPart && decimalPart !== '00') {
    result += '.' + decimalPart;
  }

  if (showCurrency) {
    result += ' ' + currency;
  }

  return result;
};

/**
 * Fiyatı kısa formatta gösterir
 * Örnek: 1234567 -> "1.23M ₺"
 */
export const formatPriceShort = (price, currency = '₺') => {
  if (price === null || price === undefined || isNaN(price)) {
    return `0 ${currency}`;
  }

  const numPrice = parseFloat(price);

  if (numPrice >= 1000000) {
    return `${(numPrice / 1000000).toFixed(2)}M ${currency}`;
  } else if (numPrice >= 1000) {
    return `${(numPrice / 1000).toFixed(2)}K ${currency}`;
  }

  return formatPrice(numPrice, { showDecimals: false, currency });
};

/**
 * Metrekare fiyatını formatlar
 */
export const formatPricePerSqm = (price, currency = '₺') => {
  return `${formatPrice(price, { currency })} / m²`;
};

/**
 * Tarihi Türkçe formatta gösterir
 */
export const formatDate = (date, options = {}) => {
  const {
    showTime = false,
    format = 'long', // 'short', 'long', 'full'
  } = options;

  if (!date) return '-';

  const dateObj = new Date(date);

  if (isNaN(dateObj.getTime())) return '-';

  const dateOptions = {
    short: { day: '2-digit', month: '2-digit', year: 'numeric' },
    long: { day: 'numeric', month: 'long', year: 'numeric' },
    full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
  };

  let formatted = dateObj.toLocaleDateString('tr-TR', dateOptions[format]);

  if (showTime) {
    const time = dateObj.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
    formatted += ` ${time}`;
  }

  return formatted;
};

/**
 * Telefon numarasını formatlar
 */
export const formatPhone = (phone) => {
  if (!phone) return '-';

  // Sadece rakamları al
  const cleaned = phone.replace(/\D/g, '');

  // Türk telefon formatı: +90 (5XX) XXX XX XX
  if (cleaned.length === 11 && cleaned.startsWith('90')) {
    return `+90 (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)} ${cleaned.slice(8, 10)} ${cleaned.slice(10)}`;
  } else if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
  }

  return phone;
};

/**
 * Alan (m²) formatlar
 */
export const formatArea = (area, unit = 'm²') => {
  if (area === null || area === undefined || isNaN(area)) {
    return '-';
  }

  return `${parseFloat(area).toLocaleString('tr-TR')} ${unit}`;
};

/**
 * Yüzde formatlar
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  return `${parseFloat(value).toFixed(decimals)}%`;
};

/**
 * Sayıyı formatlar (binlik ayraç ile)
 */
export const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) {
    return '0';
  }

  return parseFloat(number).toLocaleString('tr-TR');
};

/**
 * Metni kısaltır
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;

  return text.substring(0, maxLength) + '...';
};

/**
 * İlk harfleri büyük yapar
 */
export const capitalize = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Tüm kelimelerin ilk harfini büyük yapar
 */
export const capitalizeWords = (text) => {
  if (!text) return '';
  return text.split(' ').map(capitalize).join(' ');
};

/**
 * Boş değer kontrolü ve varsayılan değer döndürme
 */
export const defaultValue = (value, defaultVal = '-') => {
  if (value === null || value === undefined || value === '') {
    return defaultVal;
  }
  return value;
};

/**
 * Durum badge'i için renk döndürür
 */
export const getStatusColor = (status) => {
  const colors = {
    'active': 'green',
    'sold': 'red',
    'deposit': 'orange',
    'option': 'blue',
    'manager_option': 'purple',
    'rented': 'gray',
    'Satışta': 'green', // Legacy support
    'Satıldı': 'red',
    'Kaporalı': 'orange',
    'Opsiyonlu': 'blue',
    'Kiralık': 'purple',
    'Kiralandı': 'gray',
    'Aktif': 'green',
    'Pasif': 'gray',
    'Beklemede': 'yellow',
  };

  return colors[status] || 'gray';
};

/**
 * Status değerini Türkçe'ye çevirir
 */
export const formatStatus = (status) => {
  const statusMap = {
    'active': 'Aktif',
    'sold': 'Satıldı',
    'deposit': 'Kaporalı',
    'option': 'Opsiyonlu',
    'manager_option': 'Yönetici Opsiyonlu',
    'rented': 'Kiralanmış',
    'new': 'Yeni',
    'second_hand': 'İkinci El',
    'rental': 'Kiralık',
    'individual': 'Gerçek Kişi',
    'corporate': 'Tüzel Kişi',
    'male': 'Erkek',
    'female': 'Kadın',
    'other': 'Diğer',
    'single': 'Bekar',
    'married': 'Evli',
    'divorced': 'Boşanmış',
    'widowed': 'Dul',
    'face_to_face': 'Yüz Yüze',
    'call': 'Arama',
    'email': 'E-posta',
    'whatsapp': 'WhatsApp',
    'investment': 'Yatırım',
    'residence': 'Oturum',
    'investment_and_residence': 'Yatırım ve Oturum',
    'residential': 'Konut',
    'land': 'Arsa',
    'commercial': 'Ticari',
    'potential': 'Potansiyel',
    'qualified': 'Nitelikli',
    'new': 'Yeni',
    'negative': 'Negatif',
    'uncertain': 'Belirsiz',
    'recalled': 'Yeniden Aranan',
    'unqualified': 'Niteliksiz',
    'former': 'Eski',
    'definite_sale': 'Kesin Satış',
    'definite_rental': 'Kesin Kiralama',
    'apartment': 'Daire',
    'villa': 'Villa',
    'summer_house': 'Yazlık',
    'detached_house': 'Müstakil Ev',
    'farm_house': 'Çiftlik Evi',
    'mansion': 'Köşk',
    'waterside_mansion': 'Yalı',
    'residence': 'Residence',
    'duplex': 'Dublex',
    'studio': 'Stüdyo',
    'north': 'Kuzey',
    'south': 'Güney',
    'east': 'Doğu',
    'west': 'Batı',
    'northeast': 'Kuzeydoğu',
    'northwest': 'Kuzeybatı',
    'southeast': 'Güneydoğu',
    'southwest': 'Güneybatı'
  };

  return statusMap[status] || status;
};

/**
 * Dosya boyutunu formatlar
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Relatif zaman formatı (örn: "2 saat önce")
 */
export const formatRelativeTime = (date) => {
  if (!date) return '-';

  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Az önce';
  if (diffMins < 60) return `${diffMins} dakika önce`;
  if (diffHours < 24) return `${diffHours} saat önce`;
  if (diffDays < 7) return `${diffDays} gün önce`;

  return formatDate(date, { format: 'short' });
};
